// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  ICustomerSF,
  IRecallSF,
  IRevisionServiceSF,
  IScheduleConfigSF,
  ITemplateConfigSF,
  IVehicleSF,
  IInfoPUDSF,
  IcampaignsF,
  IOptionalServiceSF,
  ISalesForceJson,
  TemplateMessageSF,
  IScheduleDefault,
  IVehicle,
  IDealerSF,
  IDealer,
  IResponseModel,
  ICoordinates,
  IComplementItensSF,
} from '@agenda-customer-ui/shared';
import { IUserProfileResponse } from '@core/interface';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, firstValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { API, environment } from '@core/envs';

interface ISend {
  cancelation: (ISalesForce: Partial<ISalesForceJson>) => void;
  scheduling: (ISalesForce: Partial<ISalesForceJson>) => void;
}

interface ISalesForceEngine {
  createSalesForceObject: (
    args: {
      user: IUserProfileResponse;
      vehicleUser: IVehicle;
      dealerInfo: IDealer;
      schedule: IScheduleDefault;
      revisionService?: IRevisionServiceSF;
      infoPUD?: IInfoPUDSF;
      campaigns?: IcampaignsF;
      optionalService?: IOptionalServiceSF;
      recallService?: IRecallSF;
      iolmParts?: IComplementItensSF;
    },
    country: string,
    templateType: TemplateMessageSF,
    coordinates: ICoordinates
  ) => Partial<ISalesForceJson>;
  getDealerObject: (
    dealerInfo: IDealer,
    coordinates: ICoordinates
  ) => IDealerSF;
  getScheduleObject: (schedule: IScheduleDefault) => IScheduleConfigSF;
  getVehicleObject: (vehicleUser: IVehicle) => IVehicleSF;
  getTemplateConfig: (
    country: string,
    templateType: TemplateMessageSF
  ) => ITemplateConfigSF;
}
@Injectable({
  providedIn: 'root',
})
export class NotificationsAPIService implements ISend, ISalesForceEngine {
  constructor(
    private http: HttpClient,
    private translate: TranslateService
  ) { }

  connect(json: Partial<ISalesForceJson>): Observable<IResponseModel<string>> {
    const api = environment.getRoute(API.notifications);
    const url = `${api}/email-notification/salesforce-online-email`;

    return this.http.post<IResponseModel<string>>(url, json);
  }

  async cancelation({
    vehicle,
    customer,
    scheduleConfig,
    templateConfig,
    dealer,
  }: Partial<ISalesForceJson>) {
    if (
      !customer ||
      !scheduleConfig ||
      !templateConfig ||
      !dealer
    ) {
      throw RangeError(
        'cancelation = {dealer, customer, scheduleConfig, templateConfig} required'
      );
    }

    scheduleConfig.Service_Status = 'cancelado';

    const dto = {
      ...templateConfig,
      ...scheduleConfig,
      ...customer,
      ...vehicle,
      ...dealer,
    } as Partial<ISalesForceJson>;

    await firstValueFrom(this.connect(dto));
  }

  async scheduling({
    vehicle,
    customer,
    scheduleConfig,
    templateConfig,
    revisionService,
    infoPUD,
    campaigns,
    optionalService,
    recallService,
    dealer,
    iolmParts,
    marketing
  }: Partial<ISalesForceJson>) {
    if (
      !customer ||
      !dealer ||
      !scheduleConfig ||
      !templateConfig
    ) {
      throw RangeError(
        'scheduling = {dealer, customer, scheduleConfig, templateConfig} required'
      );
    }

    scheduleConfig.Service_Status = 'confirmado';

    const dto = {
      ...templateConfig,
      ...scheduleConfig,
      ...customer,
      ...vehicle,
      ...revisionService,
      ...infoPUD,
      ...campaigns,
      ...optionalService,
      ...recallService,
      ...dealer,
      ...iolmParts,
      ...marketing,
    } as Partial<ISalesForceJson>;

    await firstValueFrom(this.connect(dto));
  }

  createSalesForceObject(
    args: {
      user: IUserProfileResponse;
      vehicleUser: IVehicle;
      dealerInfo: IDealer;
      schedule: IScheduleDefault;
      revisionService?: IRevisionServiceSF;
      infoPUD?: IInfoPUDSF;
      campaigns?: IcampaignsF;
      optionalService?: IOptionalServiceSF;
      recallService?: IRecallSF;
      iolmParts?: IComplementItensSF;
    },
    country: string,
    templateType: TemplateMessageSF,
    coordinates?: ICoordinates
  ): Partial<ISalesForceJson> {
    const {
      user,
      vehicleUser,
      schedule,
      dealerInfo,
      revisionService,
      infoPUD,
      campaigns,
      optionalService,
      recallService,
      iolmParts,
    } = args;

    let salesForceObject = {} as ISalesForceJson;

    const templateConfig = this.getTemplateConfig(country, templateType);
    if (templateConfig) {
      salesForceObject = {
        ...salesForceObject,
        templateConfig,
      };
    }

    if (user) {
      const customer = this.#getInfoUser(user);
      salesForceObject = {
        ...salesForceObject,
        customer,
      };
    }

    const vehicle = this.getVehicleObject(vehicleUser);
    salesForceObject = {
      ...salesForceObject,
      vehicle,
    };

    if (schedule) {
      const scheduleConfig = this.getScheduleObject(schedule);
      salesForceObject = {
        ...salesForceObject,
        scheduleConfig,
      };
    }

    if (dealerInfo) {
      const dealer = this.getDealerObject(dealerInfo, coordinates);
      salesForceObject = {
        ...salesForceObject,
        dealer,
      };
    }

    if (revisionService) {
      salesForceObject = {
        ...salesForceObject,
        revisionService,
      };
    }

    if (infoPUD) {
      salesForceObject = {
        ...salesForceObject,
        infoPUD,
      };
    }

    if (campaigns) {
      salesForceObject = {
        ...salesForceObject,
        campaigns,
      };
    }

    if (optionalService) {
      salesForceObject = {
        ...salesForceObject,
        optionalService,
      };
    }

    if (recallService) {
      salesForceObject = {
        ...salesForceObject,
        recallService,
      };
    }

    if (iolmParts) {
      salesForceObject = {
        ...salesForceObject,
        iolmParts,
      };
    }

    return salesForceObject;
  }

  getScheduleObject(schedule: IScheduleDefault): IScheduleConfigSF {
    return {
      Date: this.#formattedDateDefault(schedule.scheduleDate),
      Time: this.#formatTime(schedule.scheduleHour),
      Save_to_Calendar_Link: schedule.linkCalendar ?? '',
      Total: schedule.totalDiscount ? schedule.total : '',
      Total_Discount: schedule.totalDiscount
        ? schedule.totalDiscount
        : schedule.total,
      Total_Wo_Tax: schedule.totalWithoutTax ?? '',
      Schedule_source: 'OSR',
      Schedule_Id: schedule.bookingID ?? '',
      Service_Status: '',
      Schedule_Datetime: this.#getFormattedDateTime(new Date()),
    };
  }

  #getFormattedDateTime(date: Date): string {
    // Formata a data para o formato dd/mm/aaaa hh:mm:ss AM/PM
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    let hour = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    const ampm = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12;
    hour = hour ? hour : 12; // 0 should be 12
    const hourStr = String(hour).padStart(2, '0');

    return `${month}/${day}/${year} ${hourStr}:${minutes}:${seconds} ${ampm}`;
  }

  getDealerObject(dealerInfo: IDealer, coordinates?: ICoordinates): IDealerSF {
    const coordinatesMap = coordinates
      ? coordinates
      : (dealerInfo.coordinates as ICoordinates);
    return {
      Dealer_Name: dealerInfo.name,
      Dealer_Address: dealerInfo.address,
      Service_Advisor: dealerInfo.advisor,
      Dealer_Address_Link: coordinatesMap
        ? this.createLinkMaps(coordinatesMap)
        : 'https://www.ford.com',
    };
  }

  getVehicleObject(vehicleUser: IVehicle): IVehicleSF {
    const { plate, licensePlate, modelName, vin, chassis } = vehicleUser ?? {};
    const plateVehicle = plate ?? licensePlate;
    return {
      Vehicle_Name: modelName ?? '',
      License_Plate: plateVehicle ?? '',
      VIN: vin ?? chassis,
      VIN_odometer: vehicleUser?.odometer?.toString() ?? '',
      TMA: vehicleUser?.model ?? '',
      Model_Year: vehicleUser?.modelYear?.toString() ?? '',
    };
  }

  getTemplateConfig(
    country: string,
    templateType: TemplateMessageSF
  ): ITemplateConfigSF {
    return {
      Template_message: templateType,
      Market: country,
    };
  }

  createLinkMaps(coords: ICoordinates): string {
    return `https://www.google.com/maps/search/?api=1&query=${coords.lat},${coords.lng}&`;
  }

  // TODO: trocar por um export padrao para todos projetos
  #formattedDateDefault = (date: string): string => {
    const newDate = new Date(date);
    if (isNaN(newDate.getTime())) {
      return '';
    }
    const year = newDate.getFullYear();
    const month = (newDate.getUTCMonth() + 1).toString().padStart(2, '0');
    const day = newDate.getUTCDate().toString().padStart(2, '0');
    return `${day}/${month}/${year}`;
  };

  // TODO: trocar por um export padrao para todos projetos
  #formatTime = (time: number) => {
    let key = '';
    if (time > -1) {
      const timePadded = String(time).padStart(4, '0');
      key = timePadded.substring(0, 2) + ':' + timePadded.substring(2, 4);
    }
    return key;
  };

  // TODO MAPEA AS FUNCOES REPETIDAS
  #getInfoUser(user: IUserProfileResponse): ICustomerSF {
    return {
      Customer_GUID: user.userGuid ?? '',
      EmailAddress: user.emails?.email ?? '',
      SubscriberKey: user.emails?.email ?? '',
      Customer_Cellphone: user.phoneNumbers?.phoneNumber ?? '',
      FirstName: user.names?.firstName ?? '',
      nationalId: user.nationalIdentification?.value ?? '',
      Customer_Comment: '',
      Document_Number: user.nationalIdentification?.value ?? '',
    };
  }
}
